import { useReducedMotion } from "framer-motion";
import { lazy, Suspense, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const ConfettiPlayer = lazy(() => import("./ConfettiPlayer"));

export interface ConfettiProps {
  /** Monta o confete. Volta a tocar sempre que passa de `false` para `true`. */
  open: boolean;
  /** Chamado quando a animação termina — quem abriu fecha aqui. */
  onComplete: () => void;
}

/**
 * Confete por cima da tela inteira, uma vez só, para celebrar uma ação que
 * **já deu certo** (o saque saiu). Não é aviso: a confirmação em texto continua
 * sendo do toast, e o confete só acompanha.
 *
 * Não bloqueia clique nenhum (`pointer-events-none`), fica acima de modal e
 * tooltip e sai sozinho ao terminar. Com `prefers-reduced-motion` não aparece:
 * `onComplete` é chamado na hora.
 */
export const Confetti = ({ open, onComplete }: ConfettiProps) => {
  const reduzirMovimento = useReducedMotion();
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (open && reduzirMovimento) onCompleteRef.current();
  }, [open, reduzirMovimento]);

  if (!open || reduzirMovimento) return null;

  return createPortal(
    <div aria-hidden className="pointer-events-none fixed inset-0 z-[1300]">
      <Suspense fallback={null}>
        <ConfettiPlayer onComplete={() => onCompleteRef.current()} />
      </Suspense>
    </div>,
    document.body
  );
};
